<template>
  <div style="width: 100%">
    <slot />
    <div class="management-section-wrapper">
      <v-card-title>
        <span class="management-section-title" style="color: #000000">
          {{ $t("WorkspaceSelection") }}
        </span>
        <v-spacer />
        <v-text-field
          v-if="!loading"
          :value="searchFromParent"
          @input="updateSearchInParent"
          append-icon="search"
          :label="$t('Search')"
          :placeholder="$t('Search')"
          class="data-table-search"
          single-line
          hide-details
        />
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="filterWorkspaces"
        :loading="loading"
        loading-text=""
        :page.sync="pagination.page"
        :sort-by="pagination.sortBy"
        :sort-desc="pagination.descending"
        :items-per-page.sync="paginationWorkspacesList.rowsPerPage"
        :items-per-page-text="rowsPerPageText"
        :items-per-page-items="rowsPerPageItems"
        :class="{ 'no-show-permission': !userHasPermissionTo('show') }"
        :footer-props="{ prevIcon, nextIcon,'items-per-page-options':rowsPerPageItems }"
        @pagination="setRowsOnLocalStorage(dataTableWorkspacesListId,paginationWorkspacesList.rowsPerPage)"
        hide-default-header
        id="workspace-list"
      >
        <template v-slot:header>
          <thead>
            <tr>
              <table-header-cell
                :header="headers[0]"
                @sort="changeSort"
                :pagination="pagination"
                :filterActive="!!filters.label"
                :sort-icon="
                  pagination.descending &&
                  headers[0].value === pagination.sortBy
                    ? 'arrow_drop_up'
                    : 'arrow_drop_down'
                "
                style="padding-left: 39px"
              >
                <v-text-field
                  v-model="filters.label"
                  :label="$tc('Label', 1)"
                  clearable
                  class="text-field"
                />
              </table-header-cell>

              <table-header-cell
                :header="headers[1]"
                @sort="changeSort"
                :pagination="pagination"
                :filterActive="!!filters.plan_name"
                :sort-icon="
                  pagination.descending &&
                  headers[1].value === pagination.sortBy
                    ? 'arrow_drop_up'
                    : 'arrow_drop_down'
                "
              >
                <v-text-field
                  v-model="filters.plan_name"
                  :label="$t('PlanName')"
                  clearable
                  class="text-field"
                />
              </table-header-cell>

              <table-header-cell
                :header="headers[2]"
                @sort="changeSort"
                :pagination="pagination"
                :filterActive="!!filters.phase"
                :sort-is-disabled="true"
              >
                <v-autocomplete
                  v-model="filters.phase"
                  :label="$t('Phase')"
                  :items="projectPhaseOptions"
                  :pagination="pagination"
                  clearable
                  class="text-field"
                />
              </table-header-cell>

              <table-header-cell
                :header="headers[3]"
                @sort="changeSort"
                :pagination="pagination"
                :filterActive="!!filters.manager"
                :sort-is-disabled="true"
              >
                <v-autocomplete
                  v-model="filters.manager"
                  :label="$t('Manager')"
                  :items="getManagerOptions"
                  :pagination="pagination"
                  clearable
                  class="text-field"
                />
              </table-header-cell>

              <table-header-cell
                :header="headers[4]"
                @sort="changeSort"
                :pagination="pagination"
                :sort-icon="
                  pagination.descending &&
                  headers[5].value === pagination.sortBy
                    ? 'arrow_drop_up'
                    : 'arrow_drop_down'
                "
              />

              <table-header-cell
                :header="headers[5]"
                @sort="changeSort"
                :pagination="pagination"
                :filterActive="
                  filters.status !== null && filters.status !== undefined
                "
                :sort-icon="
                  pagination.descending &&
                  headers[6].value === pagination.sortBy
                    ? 'arrow_drop_up'
                    : 'arrow_drop_down'
                "
              >
                <v-autocomplete
                  v-model="filters.status"
                  :label="$t('Status')"
                  :items="statusOptions"
                  :pagination="pagination"
                  clearable
                  class="text-field"
                />
              </table-header-cell>
            </tr>
          </thead>
        </template>

        <template v-slot:item="props" v-if="!loading">
          <tr
            :class="{ 'cursor-pointer': userHasPermissionTo('show') }"
            @click="setWorkspace(props.item.wp_uuid)"
          >
            <td>{{ props.item.wp_label }}</td>

            <td>
              {{ props.item.wp_plan_name ? props.item.wp_plan_name : "-" }}
            </td>

            <td v-if="userHasListPermission">
              {{ getProjectPhaseName(props.item.wp_status) }}
            </td>

            <td>
              <v-layout v-if="props.item.manager">
                <img
                  :src="props.item.manager.image"
                  class="avatar data-table-logo"
                />
                <span class="data-table-td-content">
                  {{ getName(props.item.manager) }}
                </span>
              </v-layout>
              <span v-else>-</span>
            </td>

            <td>{{ props.item.wp_created_at }}</td>

            <td>
              <div
                class="user-status-icon"
                :class="{ 'status-inactive': !props.item.wp_status_active }"
              />
              <span>
                {{
                  props.item.wp_status_active
                    ? $tc("OpenNoun", 1)
                    : $tc("ClosedNoun", 1)
                }}
              </span>
            </td>
          </tr>
        </template>

        <template v-slot:pageText="props">
          {{ props.pageStart }} - {{ props.pageStop }} {{ $t("tables.Of") }}
          {{ props.itemsLength }}
        </template>

        <template v-slot:no-data>
          <div v-if="loading" />

          <v-alert v-else class="data-table-no-data" icon="warning">
            {{ $t("NoDataWorkspace") }}
          </v-alert>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import DataTableNoResults from "../_Atoms/DataTableNoResults";
import TableHeaderCell from "@/components/FieldManagement/FieldWork/Data/ActivityListTableHeaderCell";
import { listsMixin } from "@/mixins/listsMixin";
import { userListsMixin } from "@/mixins/management/userListsMixin";
import utils from "@/utils";

export default {
  name: "WorkspaceList",

  components: {
    DataTableNoResults,
    TableHeaderCell,
  },

  mixins: [managementDatatableMixin, listsMixin, userListsMixin],

  data() {
    return {
      permissionName: "workspace",

      data: null,
      workspaces: [],
      workspacesFilter: [],

      pagination: {
        sortBy: "",
        descending: false,
        rowsPerPage: 10,
      },
      dataTableWorkspacesListId: "dataTableWorkspacesListId" ,
      paginationWorkspacesList: {
        rowsPerPage: this.getRowsFromLocalStorage("dataTableWorkspacesListId")
      },
      projectPhases: [],
      projectPhaseOptions: [],
      managerOptions: [],

      filters: {
        label: undefined,
        plan_name: undefined,
        phase: undefined,
        manager: undefined,
        status: undefined,
      },

      loading: true,
    };
  },

  computed: {
    mainHeaders() {
      let headers = [
        {
          text: this.$i18n.tc("Label", 1),
          align: "left",
          value: "wp_label",
          hasFilter: true,
          width: this.userHasListPermission ? "20%" : "20%",
        },
        {
          text: this.$i18n.t("PlanName"),
          align: "left",
          value: "wp_plan_name",
          hasFilter: true,
          width: this.userHasListPermission ? "15%" : "20%",
        },
        {
          text: this.$i18n.t("Manager"),
          align: "left",
          value: "wp_manager",
          hasFilter: true,
          width: this.userHasListPermission ? "25%" : "25%",
        },
        {
          text: this.$i18n.t("CreationDate"),
          align: "left",
          value: "wp_created_at",
          hasFilter: false,
          width: "15%",
        },
        {
          text: this.$i18n.t("Status"),
          align: "left",
          value: "wp_status_active",
          hasFilter: true,
          width: "15%",
        },
      ];

      if (this.userHasListPermission) {
        headers.splice(2, 0, {
          text: this.$i18n.t("Phase"),
          align: "left",
          value: "wp_status",
          hasFilter: true,
          width: "20%",
        });
      }

      return headers;
    },

    statusOptions() {
      return [
        { value: true, text: this.$i18n.tc("OpenNoun", 1) },
        { value: false, text: this.$i18n.tc("ClosedNoun", 1) },
      ];
    },

    filterWorkspaces() {
      let conditions = [];

      if (this.filters.label) {
        conditions.push(this.filterWorkspaceLabel);
      }

      if (this.filters.plan_name) {
        conditions.push(this.filterWorkspacePlanName);
      }

      if (this.filters.phase) {
        conditions.push(this.filterWorkspacePhase);
      }

      if (this.filters.manager) {
        conditions.push(this.filterWorkspaceManager);
      }

      if (this.filters.status !== null && this.filters.status !== undefined) {
        conditions.push(this.filterWorkspaceStatus);
      }

      if (conditions.length > 0) {
        return this.workspacesFilter.filter((workspace) => {
          return conditions.every((condition) => {
            return condition(workspace);
          });
        });
      }

      return this.workspacesFilter;
    },

    getManagerOptions() {
      if (this.workspaces.length > 0) {
        for (let i = 0; i < this.workspaces.length; i++) {
          let manager = this.workspaces[i].manager;
          if (manager) {
            let user = this.managerOptions.find(
              (managerOption) => managerOption.value === manager.id
            );
            if (user === undefined) {
              this.managerOptions.push(this.sortUserOptionByWeb(manager));
            }
          }
        }
      }
      return this.managerOptions;
    },

    userHasListPermission() {
      return this.userHasFibercloudPermission("list_list");
    },
  },

  watch: {
    searchFromParent(value) {
      this.workspacesFilter = this.workspaces.filter(
        (workspace) =>
          (workspace.wp_label &&
            workspace.wp_label
              .toLowerCase()
              .includes(String(value.trim()).toLowerCase())) ||
          (workspace.wp_plan_name &&
            workspace.wp_plan_name
              .toLowerCase()
              .includes(String(value.trim()).toLowerCase()))
      );
    },
  },

  mounted() {
    this.getProjectPhases();
    this.loadProjectPhases();
    this.getWorkspaces();
  },

  methods: {
    ...mapActions([
      "fetchAllWorkspaces",
      "fetchUserAndCompanyWorkspaces",
      "fetchUserCompanies",
      "fetchUsersByCompanyID",
      "fetchWorkspaceByUUID",
      "setCurrentWorkspace",
      "RESET_NETWORK_DIAGRAM_STATE",
    ]),

    async getWorkspaces() {
      try {
        this.loading = true;
        if (this.isSuperAdmin) {
          this.workspaces = await this.fetchAllWorkspaces({
            includes: "manager",
            fields:
              "wp_uuid,wp_label,wp_id,wp_updated_at,wp_created_at,wp_status_active,wp_access_active,wp_status,wp_plan_name,wp_manager",
          });
        } else {
          this.workspaces = await this.fetchUserAndCompanyWorkspaces();
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (this.workspaces) {
          this.sortWorkspaces();
          this.workspacesFilter = this.workspaces;
          this.$nextTick(() => {
            this.updateLocalPagination(this.paginationFromParent);
            this.loading = false;
          });
        }
      }
    },

    sortWorkspaces() {
      if (this.pagination.sortBy === "" && this.workspaces.length > 0) {
        this.workspaces.sort((a, b) => {
          if (a.wp_status_active === b.wp_status_active) {
            return a.wp_label.localeCompare(b.wp_label);
          }
          return b.wp_status_active - a.wp_status_active;
        });
      }
    },

    setWorkspace(workspaceUUID) {
      this.fetchWorkspaceByUUID({
        uuid: workspaceUUID,
        includes: "engineering_rule",
        fields: "wp_uuid,wp_label,wp_id,wp_geo_json,wp_engineering_rule_id",
      })
        .then((workspace) => {
          this.setCurrentWorkspace(workspace).then(() => {
            this.RESET_NETWORK_DIAGRAM_STATE();
            if (this.$route.name !== "home") {
              this.$router.push({
                name: "home",
                params: { workspace_uuid: workspace.wp_uuid },
              });
            }
          });
        })
        .catch((error) => {
          let errorHandle = utils.errorHandler(error);
          if (errorHandle && typeof errorHandle === "string") {
            this.$toasted.global.toast_error(errorHandle);
          }
        });
    },

    getProjectPhases() {
      this.projectPhases = this.$store.getters.optionLists_phase;
    },

    loadProjectPhases() {
      let projectPhaseOptions = [];
      let projectPhases = this.$store.getters.optionLists_phase;
      for (const projectPhase of projectPhases) {
        projectPhaseOptions.push({
          text: projectPhase.client_label
            ? projectPhase.client_label
            : projectPhase.libelle,
          value: projectPhase.code,
        });
      }
      this.projectPhaseOptions = projectPhaseOptions;
    },

    getProjectPhaseName(id) {
      if (id && this.projectPhases.length > 0) {
        let phase = this.projectPhases.find((item) => item.code === id);
        return this.getListElementLabel(phase);
      } else return id;
    },

    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },

    filterWorkspacePhase(item) {
      return item.wp_status === this.filters.phase;
    },

    filterWorkspaceLabel(item) {
      return item.wp_label
        .toLowerCase()
        .includes(this.filters.label.toLowerCase());
    },

    filterWorkspacePlanName(item) {
      return (
        item.wp_plan_name &&
        item.wp_plan_name
          .toLowerCase()
          .includes(this.filters.plan_name.toLowerCase())
      );
    },

    filterWorkspaceManager(item) {
      return item.wp_manager && item.wp_manager === this.filters.manager;
    },

    filterWorkspaceStatus(item) {
      return item.wp_status_active === this.filters.status;
    },

    sortUserOptionByWeb(user) {
      let label = user.first_name;
      if (user.last_name) {
        label += " " + user.last_name;
      }
      return {
        value: user.id,
        text: label,
      };
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/Workspaces/WorkspacesList.vue